import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import { trackBlogEvent, trackButtonClick, reportViewContent } from '../utils/tracking';
import { recordBlogCtaClick, captureFirstBlogPostViewed, recordBlogPostView } from '../utils/sourceTracking';
import { standardUserApi } from '../services/standardUserApi';

const CTA_BUTTON_STYLE = {
  display: 'inline-block',
  background: 'linear-gradient(135deg, #8B5FBF, #2c2e96)',
  color: '#fff',
  padding: '14px 32px',
  borderRadius: '25px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 2px 10px rgba(139, 95, 191, 0.2)',
};

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [otherPosts, setOtherPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchOtherPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`/api/blog/posts/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Post not found');
        }
        throw new Error('Failed to fetch post');
      }
      const data = await response.json();
      setPost(data);
      trackBlogEvent('Viewed', { blogSlug: data.slug, blogTitle: data.title });
      reportViewContent();

      // Capture the view immediately, automatically, with no click required:
      // - first-touch single value (survives /events overwriting landing_page)
      // - full history array (backfilled into the DB the moment this visitor
      //   creates an account, in case they're not logged in yet - see
      //   viewedBlogSlugs in CreateAccount.js)
      captureFirstBlogPostViewed(data.slug);
      recordBlogPostView(data.slug);

      // Already logged in? Skip the localStorage round-trip and write the
      // durable, account-scoped record straight to the DB now - this is the
      // case localStorage alone can't cover (a different browser/device, or
      // storage cleared, between this view and any later signup/checkout).
      const token = localStorage.getItem('memberstackToken');
      if (token) {
        standardUserApi.recordBlogView(token, data.slug).catch(() => {
          // Non-essential for the page itself - the localStorage copy above
          // still lets this backfill later if this call fails.
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherPosts = async () => {
    try {
      const response = await fetch('/api/blog/posts');
      if (!response.ok) return;
      const data = await response.json();
      setOtherPosts((data || []).filter((p) => p.slug !== slug).slice(0, 3));
    } catch (err) {
      // Non-essential section - fail silently
    }
  };

  const handleCtaClick = (ctaLocation) => {
    recordBlogCtaClick(slug);
    trackButtonClick('Browse Gatherings', ctaLocation, { blog_slug: slug });
  };

  // Internal links (e.g. to a theme landing page) should navigate within
  // the site and count as a CTA click for attribution; genuinely external
  // links keep opening in a new tab so readers don't lose their place here.
  const isInternalLink = (href) => {
    if (!href) return false;
    if (href.startsWith('/')) return true;
    try {
      return new URL(href, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
        <SiteHeader />
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
          <h1>{error === 'Post not found' ? 'Post not found' : 'Oops!'}</h1>
          {error && error !== 'Post not found' && <p style={{ color: 'red' }}>{error}</p>}
          <Link to="/blog" style={{ color: '#6B46C1', textDecoration: 'none', fontWeight: '600' }}>
            &larr; Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      <SiteHeader />
      <main className="main-wrapper">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '30px 20px' }}>
          {/* Breadcrumb */}
          <p style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '24px' }}>
            <Link to="/" style={{ color: '#6B46C1', textDecoration: 'none' }}>Home</Link>
            {' / '}
            <Link to="/blog" style={{ color: '#6B46C1', textDecoration: 'none' }}>Blog</Link>
            {' / '}
            <span>{post.title}</span>
          </p>

          {/* Post header */}
          <header style={{ marginBottom: '20px' }}>
            <h1 style={{
              margin: '0 0 10px 0',
              color: '#1F2937',
              fontSize: '2.25rem',
              fontWeight: '800',
              lineHeight: '1.2'
            }}>
              {post.title}
            </h1>

            <p style={{ color: '#6B7280', fontSize: '0.95rem', margin: 0 }}>
              {post.persona_first_name && <span>By {post.persona_first_name} &bull; </span>}
              Published on {formatDate(post.created_at)}
              {post.updated_at && post.updated_at !== post.created_at && (
                <span> &bull; Updated {formatDate(post.updated_at)}</span>
              )}
            </p>
          </header>

          {/* Early CTA - catches readers who decide quickly, before the full article */}
          <div style={{
            background: '#f0fdf4',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '36px',
            textAlign: 'center',
          }}>
            <p style={{ margin: '0 0 14px 0', color: '#1F2937', fontWeight: '600' }}>
              Ready to meet people in person? Browse OkGather's upcoming gatherings.
            </p>
            <Link to={`/events?blog_ref=${encodeURIComponent(slug)}`} style={CTA_BUTTON_STYLE} onClick={() => handleCtaClick('blog_post_top')}>
              Browse Gatherings
            </Link>
          </div>

          {/* Post content */}
          <article style={{
            lineHeight: '1.7',
            fontSize: '1.05rem',
            color: '#333'
          }}>
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 style={{ color: '#1F2937', marginTop: '40px', marginBottom: '20px', fontSize: '1.75rem', fontWeight: '800' }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 style={{ color: '#1F2937', marginTop: '35px', marginBottom: '15px', fontSize: '1.5rem', fontWeight: '700' }}>
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 style={{ color: '#1F2937', marginTop: '30px', marginBottom: '15px', fontSize: '1.25rem', fontWeight: '700' }}>
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p style={{ marginBottom: '20px', lineHeight: '1.7' }}>
                    {children}
                  </p>
                ),
                a: ({ children, href }) => {
                  if (isInternalLink(href)) {
                    return (
                      <a
                        href={href}
                        onClick={() => handleCtaClick('blog_post_inline_link')}
                        style={{ color: '#6B46C1', textDecoration: 'underline', fontWeight: '600' }}
                      >
                        {children}
                      </a>
                    );
                  }
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#6B46C1', textDecoration: 'none' }}
                    >
                      {children}
                    </a>
                  );
                },
                code: ({ children, className }) => {
                  const isInline = !className;
                  return (
                    <code style={{
                      backgroundColor: isInline ? '#f4f4f4' : '#f8f8f8',
                      padding: isInline ? '2px 4px' : '15px',
                      borderRadius: '4px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      fontSize: isInline ? '0.9em' : '0.85em',
                      display: isInline ? 'inline' : 'block',
                      border: isInline ? 'none' : '1px solid #e0e0e0',
                      overflowX: 'auto'
                    }}>
                      {children}
                    </code>
                  );
                },
                blockquote: ({ children }) => (
                  <blockquote style={{
                    borderLeft: '4px solid #8B5FBF',
                    paddingLeft: '20px',
                    margin: '20px 0',
                    fontStyle: 'italic',
                    color: '#666'
                  }}>
                    {children}
                  </blockquote>
                )
              }}
            >
              {post.content}
            </ReactMarkdown>
          </article>

          {/* End-of-post CTA - the strongest one, right after they've read the value */}
          <div style={{
            background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
            border: '1px solid rgba(139, 95, 191, 0.15)',
            borderRadius: '16px',
            padding: '36px 28px',
            marginTop: '48px',
            textAlign: 'center',
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#1F2937', fontSize: '1.4rem', fontWeight: '800' }}>
              Ready to give it a try?
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#4B5563' }}>
              One membership gets you into every OkGather gathering - browse what's coming up near you.
            </p>
            <Link to={`/events?blog_ref=${encodeURIComponent(slug)}`} style={CTA_BUTTON_STYLE} onClick={() => handleCtaClick('blog_post_bottom')}>
              Browse Gatherings
            </Link>
          </div>

          {/* More from the blog */}
          {otherPosts.length > 0 && (
            <div style={{ marginTop: '56px' }}>
              <h3 style={{ color: '#1F2937', fontSize: '1.25rem', fontWeight: '700', marginBottom: '18px' }}>
                More from the blog
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {otherPosts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div style={{
                      padding: '18px 22px',
                      borderRadius: '12px',
                      border: '1px solid rgba(139, 95, 191, 0.15)',
                      backgroundColor: '#fff',
                    }}>
                      <span style={{ color: '#1F2937', fontWeight: '700' }}>{p.title}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '40px', textAlign: 'center' }}>
            <Link to="/blog" style={{ color: '#6B46C1', textDecoration: 'none', fontWeight: '600' }}>
              &larr; Back to Blog
            </Link>
          </div>
        </div>

        <Footer />
      </main>
    </div>
  );
}

export default BlogPost;
